import { useState, useEffect, useCallback } from "react";
import { Sun, Moon } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import {
  applyTheme,
  ensureThemeSynchronization,
  getStoredTheme,
  persistTheme,
  resolveInitialTheme,
  subscribeSystemTheme,
} from "@/lib/theme";
import { haptics } from "@/lib/haptics";

interface Props {
  isTransparent: boolean;
  borderColor: string;
  textColor?: string;
}

const ThemeToggle = ({ isTransparent, borderColor, textColor }: Props) => {
  const { t } = useLanguage();

  const [isDark, setIsDark] = useState(() => resolveInitialTheme() === "dark");
  const [hasManualPref, setHasManualPref] = useState<boolean>(() => getStoredTheme() !== null);

  useEffect(() => {
    const nextTheme = isDark ? "dark" : "light";
    applyTheme(nextTheme);
    if (hasManualPref) persistTheme(nextTheme);
    ensureThemeSynchronization();
  }, [isDark, hasManualPref]);

  // Real-time OS sync: when the user has not manually toggled, mirror
  // any OS color-scheme flip (e.g., automatic sunset switch) instantly.
  useEffect(() => {
    return subscribeSystemTheme((mode) => {
      if (getStoredTheme() === null) {
        setIsDark(mode === "dark");
      }
    });
  }, []);

  const toggle = useCallback(() => {
    haptics.selection();
    setHasManualPref(true);
    setIsDark((v) => !v);
  }, []);

  return (
    <button
      onClick={toggle}
      className="liquid-capsule flex h-8 w-8 items-center justify-center rounded-full border border-transparent transition-all duration-token-base"
      style={{
        color: textColor ?? (isTransparent ? "hsl(var(--foreground) / 0.94)" : "hsl(var(--foreground) / 0.90)"),
        borderColor,
      }}
      aria-label={isDark ? t.theme.light : t.theme.dark}
      title={isDark ? t.theme.light : t.theme.dark}
    >
      {isDark ? (
        <Sun className="h-3.5 w-3.5" />
      ) : (
        <Moon className="h-3.5 w-3.5" />
      )}
    </button>
  );
};

export default ThemeToggle;
